import { Platform } from 'click-types';

// TypeScript interfaces
interface ScreenDimensions {
  width: number;
  height: number;
}

interface ComponentConfig {
  screens?: {
    desktop?: ScreenDimensions;
    tablet?: ScreenDimensions;
    mobile?: ScreenDimensions;
  };
  defaultWidth?: number;
  defaultHeight?: number;
  width?: number;
  height?: number;
}

type ScreenType = 'desktop' | 'tablet' | 'mobile';

export const getNewScreenSize = (platform: Platform) => {
  if (platform === Platform.MOBILE) {
    return {
      width: 375,
      height: 667,
    };
  }

  // TODO: Add Table size
  if (platform === Platform.TABLET) {
    return {
      height: 1024,
      width: 768,
    };
  }

  return {
    width: 1200,
    height: 800,
  };
};

// Screen dimensions mapping
export const SCREEN_DIMENSIONS: Record<ScreenType, ScreenDimensions> = {
  desktop: getNewScreenSize(Platform.WEB), // { width: 1200, height: 800 }
  tablet: getNewScreenSize(Platform.TABLET), // { width: 768, height: 1024 }
  mobile: getNewScreenSize(Platform.MOBILE), // { width: 375, height: 667 }
};

// Breakpoint index to screen type mapping
const BREAKPOINT_TO_SCREEN = {
  0: 'desktop',
  1: 'tablet',
  2: 'mobile',
} as const;

type BreakpointIndex = keyof typeof BREAKPOINT_TO_SCREEN;

/**
 * Get screen-specific dimensions for a component based on breakpoint index
 * @param componentConfig - Component configuration object with optional screens property
 * @param breakpointIndex - Breakpoint index (0=desktop, 1=tablet, 2=mobile)
 * @returns Screen-specific width and height
 */
export const getComponentScreenDimensions = (
  componentConfig: ComponentConfig,
  breakpointIndex: number
): ScreenDimensions => {
  const screenType =
    BREAKPOINT_TO_SCREEN[breakpointIndex as BreakpointIndex] || 'desktop';

  // If component has screen-specific configuration, use it
  if (componentConfig?.screens?.[screenType]) {
    return {
      width: componentConfig.screens[screenType]!.width,
      height: componentConfig.screens[screenType]!.height,
    };
  }

  // Fallback to default component dimensions
  return {
    width:
      componentConfig?.defaultWidth ||
      componentConfig?.width ||
      SCREEN_DIMENSIONS[screenType].width,
    height: componentConfig?.defaultHeight || componentConfig?.height || 64,
  };
};

/**
 * Get screen type from breakpoint index
 * @param breakpointIndex - Breakpoint index (0=desktop, 1=tablet, 2=mobile)
 * @returns Screen type string
 */
export const getScreenTypeFromBreakpoint = (
  breakpointIndex: number
): ScreenType => {
  return BREAKPOINT_TO_SCREEN[breakpointIndex as BreakpointIndex] || 'desktop';
};

/**
 * Get screen type from component ID suffix
 * @param id - Component ID with suffix (.0, .1, .2)
 * @returns Screen type string
 */
export const getScreenTypeFromId = (id = ''): ScreenType => {
  if (id.endsWith('.0')) return 'desktop';
  if (id.endsWith('.1')) return 'tablet';
  if (id.endsWith('.2')) return 'mobile';
  return 'desktop';
};

/**
 * Get full screen dimensions for a given screen type
 * @param screenType - Screen type ('desktop', 'tablet', 'mobile')
 * @returns Full screen dimensions
 */
export const getFullScreenDimensions = (
  screenType: ScreenType
): ScreenDimensions => {
  return SCREEN_DIMENSIONS[screenType] || SCREEN_DIMENSIONS.desktop;
};
